import './PatientListPage.scss';

import React, { useEffect, useState } from 'react';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { generatePath, useLocation, useNavigate } from 'react-router-dom';

import Badge from '@components/Badge';
import { BadgeStyle } from '@components/Badge/Badge';
import Button, { ButtonStyle } from '@components/Button';
import DataGrid, { IDataGridColumn } from '@components/DataGrid';
import { useInjection } from '@components/Injection';
import Layout from '@components/Layout';
import { Pagination, usePagination } from '@components/Pagination';
import SearchBar from '@components/SearchBar';
import { useQuery } from '@components/useQuery';
import { BraceOrderStatus } from '@constants/brace-order-status';
import { RoutePath } from '@constants/route-path';
import { IDataQueryResponse, IDataQuerySortModel } from '@models/data-query';
import { IPatientSummary } from '@models/patient';
import { BRACE_ORDERS_SERVICE, IBraceOrdersService } from '@services/brace-orders.service';
import { PATIENTS_SERVICE, PatientsService } from '@services/patients.service';
import { ColumnSort } from '@src/constants/column-sort';
import { useAuth } from '@src/contexts/authContext';

const elementsPerPage = 25;
const getColumns = (t: TFunction): IDataGridColumn<IPatientSummary>[] => [
  {
    fieldName: 'id',
    headerName: t('models.summary.id'),
    sortable: true,
    flex: 0.5,
    formatter: (row) => <p className="modText modText--body2 modText--secondary">{row.id}</p>,
  },
  {
    fieldName: 'name',
    headerName: t('models.summary.name'),
    sortable: true,
    flex: 1,
    formatter: (row) => <strong className="modText modText--body2 modText--important">{row.name}</strong>,
  },
  {
    fieldName: 'status',
    headerName: t('models.summary.status'),
    sortable: true,
    flex: 0.5,
    formatter: (row) => {
      let badgeProps: { style: BadgeStyle; iconClassName?: string };

      switch (row.status) {
        case BraceOrderStatus.TwinReady:
          badgeProps = { style: BadgeStyle.Purple };
          break;
        case BraceOrderStatus.Pending:
          badgeProps = { style: BadgeStyle.Grey /*, iconClassName: 'modIcon modIcon--loader'*/ };
          break;
        case BraceOrderStatus.Delivered:
          badgeProps = { style: BadgeStyle.Green };
          break;
        default:
          badgeProps = { style: BadgeStyle.Black };
      }

      return (
        <Badge style={badgeProps.style}>
          {badgeProps.iconClassName && <i className={badgeProps.iconClassName} />}{' '}
          {t('enums.braceOrderStatus', { context: row.status })}
        </Badge>
      );
    },
  },
  {
    fieldName: 'action',
    flex: null,
    width: 100,
    align: 'right',
    formatter: (row) => (
      <Button
        type="link"
        style={ButtonStyle.Action}
        to={generatePath(RoutePath.SinglePatientPage, { patientId: row.id })}>
        {t('general.view')}
      </Button>
    ),
  },
];

function PatientListPage() {
  const { t } = useTranslation();
  const query = useQuery();
  const location = useLocation();
  const navigate = useNavigate();

  const { pageIndex } = usePagination();
  const searchTerm = query.get('searchTerm');

  const [result, setResult] = useState<IDataQueryResponse<IPatientSummary>>();
  const [sortModel, setSortModel] = useState<IDataQuerySortModel<IPatientSummary>>();
  const [braceStatusByPatient, setBraceStatusByPatient] = useState<
    Record<string, IPatientSummary['status'] | undefined>
  >({});

  const patientsService = useInjection<PatientsService>(PATIENTS_SERVICE);
  const braceOrdersService = useInjection<IBraceOrdersService>(BRACE_ORDERS_SERVICE);

  const authContext = useAuth();
  useEffect(() => {
    patientsService
      .getSummaries({
        clinicId: authContext.userObject.clinicId,
        searchTerm: searchTerm || '',
        sortModel: sortModel || { fieldName: 'id', sort: ColumnSort.Ascending },
        startRow: pageIndex * elementsPerPage,
        endRow: (pageIndex + 1) * elementsPerPage,
      })
      .then(setResult);
  }, [pageIndex, searchTerm, sortModel]);

  // Derive latest brace status per patient on the client; if none, leave undefined (renders as "New")
  useEffect(() => {
    const loadStatuses = async () => {
      if (!result?.data || result.data.length === 0) {
        setBraceStatusByPatient({});
        return;
      }
      const entries = await Promise.all(
        result.data.map(async (row) => {
          try {
            const braces = await braceOrdersService.findAllForPatient(
              row.id,
              authContext.userObject.clinicId,
            );
            if (!braces || braces.length === 0) return [row.id, undefined] as const;
            const latest = braces.reduce(
              (acc, b) =>
                !acc || new Date(b.braceInfo.date).getTime() > new Date(acc.braceInfo.date).getTime()
                  ? b
                  : acc,
              undefined as (typeof braces)[number] | undefined,
            );
            return [row.id, latest?.status] as const;
          } catch (e) {
            return [row.id, undefined] as const;
          }
        }),
      );
      const mapping: Record<string, BraceOrderStatus | undefined> = {};
      entries.forEach(([id, status]) => {
        mapping[id] = status;
      });
      setBraceStatusByPatient(mapping);
    };
    loadStatuses();
  }, [result, authContext.userObject.clinicId]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pageIndex]);

  const formatLink = (pageNumber: number, term = searchTerm) => {
    const link = `${location.pathname}?page=${pageNumber}`;
    return term ? `${link}&searchTerm=${term}` : link;
  };

  return (
    <Layout>
      <h1 className="modTitle modTitle--h1">
        {t('patientList.title')}{' '}
        {result && <span className="modText modText--note modText--secondary">({result.totalCount})</span>}
      </h1>
      <div className="modPatientListToolbar" role="toolbar">
        <div className="modPatientListToolbar__search">
          <SearchBar
            placeholder={t('patientList.searchPlaceholder')}
            onSearch={(term) => navigate(formatLink(1, term))}
          />
          {searchTerm && (
            <Badge autoSize onClick={() => navigate(formatLink(1, ''))}>
              {searchTerm} <i className="modIcon modIcon--xmark" />
            </Badge>
          )}
        </div>
        <Button type="link" to={RoutePath.NewPatientPage}>
          <i className="modIcon modIcon--plus" /> {t('patientList.newPatient')}
        </Button>
      </div>
      {result && (
        <>
          {result.data.length > 0 && (
            <>
              <DataGrid<IPatientSummary>
                columns={getColumns(t)}
                rows={result.data.map((row) => ({
                  ...row,
                  status: (braceStatusByPatient[row.id] ?? row.status) as IPatientSummary['status'],
                }))}
                onSortingUpdated={setSortModel}
              />
              <Pagination
                activeIndex={pageIndex}
                pageCount={Math.ceil(result.totalCount / elementsPerPage)}
                formatLink={formatLink}
              />
              <p className="modPatientListCount">
                {t('patientList.countIndicator', { count: result.data.length, total: result.totalCount })}
              </p>
            </>
          )}
          {result.data.length === 0 && (
            <p className="modText modText--body1 modText--secondary modText--centered">
              {t('patientList.noData')}
            </p>
          )}
        </>
      )}
    </Layout>
  );
}

export default PatientListPage;
